import React, { useContext, useMemo, type JSX } from 'react';
import loginStateQuery from './Queries/LoginStateQuery.gql';
import { ApolloError, useQuery } from '@apollo/client';
import type {
  LoginStateQuery_me_databases as IDatabase,
  LoginStateQuery,
} from './Queries/_QueryTypes/LoginStateQuery';
import { useBaseRouteContext } from '../../../Routing/Utils/routerUtils';
import { maybeRedirectToDatabase } from '../../../../Utils/databaseRedirect';

export { IDatabase };

export interface ILoginStateContext {
  refetch: () => void;
  isLoggedIn: boolean;
  loading: boolean; //inloggningsinfo hämtas (isLoggedIn är då false till riktig status finns)
  databases: IDatabase[];
  sign: string;
  name: string;
  routeIsAtLogin: boolean;
  error: ApolloError | undefined;
}

export const LoginStateContext = React.createContext<ILoginStateContext>({
  refetch: () => {},
  isLoggedIn: false,
  loading: true,
  databases: [],
  sign: '',
  name: '',
  routeIsAtLogin: false,
  error: undefined,
});

interface IProps {
  atLoginOrStart: boolean;
  children: (isAtLogin: boolean) => JSX.Element;
}

const createContextValue = (
  data: LoginStateQuery | undefined,
  refetch: () => void,
  routeIsAtLogin: boolean,
  loading: boolean,
  error: ApolloError | undefined,
  database: string | undefined
): ILoginStateContext => {
  const databases = data?.me?.[0].databases ?? [];

  const { redirecting } = !window.env.disableRedirectState
    ? maybeRedirectToDatabase(databases, database, {
        performRedirect: false,
      })
    : { redirecting: false };

  loading = loading || redirecting;

  const sign = data?.me?.[0].sign ?? '';
  const name = data?.me?.[0].name ?? '';

  const isLoggedIn = loading ? false : sign !== '';

  return {
    refetch,
    isLoggedIn,
    loading,
    databases: data?.me?.[0].databases ?? [],
    sign,
    name,
    routeIsAtLogin,
    error,
  };
};

export const useLoginStateContext = () => useContext(LoginStateContext);

export const LoginStateProvider = (props: IProps) => {
  const [{ database }] = useBaseRouteContext();
  const { data, refetch, loading, error } =
    useQuery<LoginStateQuery>(loginStateQuery);
  const isAtLogin = props.atLoginOrStart && !data?.me?.at(0)?.sign;
  const value: ILoginStateContext = useMemo(
    () =>
      createContextValue(data, refetch, isAtLogin, loading, error, database),
    [data, refetch, isAtLogin, loading, error, database]
  );
  return (
    <LoginStateContext.Provider value={value}>
      {props.children(isAtLogin)}
    </LoginStateContext.Provider>
  );
};
